import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./components/Home";
import Admin from "./components/Admin";
import MapSearch from "./components/mapsearchpage";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import PropertyDetails from "./components/propertyDetails";
import ProtectedRoute from "./utils/ProtectedRouter";
import { Events } from "./pages/events";
import Team from "./pages/team";
import { Layout } from "./components/layout";
import { Contact } from "./pages/contact";
import { AboutUs } from "./pages/aboutUs";
import { EricNur } from "./pages/EricNur";
import BlogList from "./pages/belog";
import BlogDetail from "./pages/BlogDetail";
import { FloorPlan } from "./pages/floor-plan";
import Bordmembers from "./pages/boardMembers";
import SponsorsList from "./pages/SponsorsList";
import SponsorDetail from "./pages/SponsorDetail";
import InvestmentsList from "./pages/InvestmentsList";
import InvestmentDetail from "./pages/InvestmentDetail";
import FreeList from "./pages/FreeList";
import FreeDetail from "./pages/FreeDetail";
import { AgentAdmin } from "./pages/agentAdmin";
import { Doc } from "./pages/doc";
import { UserDashboard } from "./pages/UserDashboard";
import { ThankYou } from "./pages/thankYou";
import { useSettings } from "./hooks/useSettings";
import { Login } from "./pages/Login";
import { NavItemGuard } from "./components/NavItemGuard";
import Test1 from "./components/Test/Test1";
import Test2 from "./components/Test/Test2";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ProjectAdminDashboard from "./pages/ProjectAdminDashboard";
import { resolveAndSetCommunityFromDomain } from "./utils/communityUtils";
import CommunityInfo from "./pages/community";

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    const communityId = localStorage.getItem("communityId") || process.env.REACT_APP_COMMUNITY_ID || "1";
    const payload = JSON.stringify({
      communityId,
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer || "",
    });
    const url = `${apiUrl}/api/traffic/track`;

    // Send as text/plain (a CORS-safelisted content type) so the cross-origin
    // request goes out WITHOUT a preflight. sendBeacon cannot satisfy a
    // preflight, so an "application/json" beacon is silently dropped across
    // origins — that's why no traffic was ever recorded. Fall back to fetch
    // when sendBeacon is missing OR fails to queue (it returns false).
    let queued = false;
    try {
      if (navigator.sendBeacon) {
        queued = navigator.sendBeacon(
          url,
          new Blob([payload], { type: "text/plain;charset=UTF-8" })
        );
      }
    } catch {
      queued = false;
    }

    if (!queued) {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const [communityResolved, setCommunityResolved] = useState(false);
  const { isLoading } = useSettings();

  useEffect(() => {
    resolveAndSetCommunityFromDomain().finally(() => setCommunityResolved(true));
  }, []);

  if (!communityResolved) {
    return (
      <div className="grid h-screen place-content-center bg-white">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <PageViewTracker />
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/" element={<Layout isLoading={isLoading} />}>
          <Route path="/test1" element={<Test1 />} />
          <Route path="/Test" element={<Test2 />} />
          {/* The Team page moved from /Realtors to /Team. This redirect sits OUTSIDE
              NavItemGuard on purpose: once the NavItem rows point at /Team, /Realtors is no
              longer an allowed path and the guard would render "Not found" instead. */}
          <Route path="/Realtors" element={<Navigate to="/Team" replace />} />
          <Route element={<NavItemGuard />}>
            <Route index element={<Home />} />
            <Route path="/community" element={<CommunityInfo />} />
            <Route path="/events" element={<Events />} />
            <Route path="/Team" element={<Team />} />
            <Route path="/mapsearch" element={<MapSearch />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/eric-nur" element={<EricNur />} />
            <Route path="/Floor-Plans" element={<FloorPlan />} />
            <Route path="/Rules-And-Regulations" element={<Doc />} />
            <Route path="/board-members" element={<Bordmembers />} />
            <Route path="/sponsors" element={<SponsorsList />} />
            <Route path="/sponsors/:id" element={<SponsorDetail />} />
            <Route path="/investments" element={<InvestmentsList />} />
            <Route path="/investments/:id" element={<InvestmentDetail />} />
            <Route path="/free" element={<FreeList />} />
            <Route path="/free/:id" element={<FreeDetail />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/login" element={<Login />} />
            <Route path="/Terms-Of-Use" element={<TermsAndConditions />} />
            <Route path="/Privacy-Policy" element={<PrivacyPolicy />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/propertyDetails/:id" element={<PropertyDetails />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
          </Route>
        </Route>

        {/* PROTECTED ROUTES */}
        <Route
          element={
            <ProtectedRoute allowedRoles={["SuperAdmin", "Admin", "Manager"]} />
          }
        >
          <Route path="/admin" element={<Admin />} />
          <Route path="/project-admin" element={<ProjectAdminDashboard />} />
        </Route>

        <Route element={<ProtectedRoute allowedRoles={["SuperAdmin"]} />}>
          <Route
            path="/super-admin-dashboard"
            element={<SuperAdminDashboard />}
          />
        </Route>

        <Route
          element={
            <ProtectedRoute
              allowedRoles={["Agent", "Admin", "Manager", "SuperAdmin"]}
            />
          }
        >
          <Route path="/agent-admin" element={<AgentAdmin />} />
        </Route>

        <Route element={<ProtectedRoute allowedRoles={["public"]} />}>
          <Route path="/user-dashboard" element={<UserDashboard />} />
        </Route>

        {/* HANDLE 404 PAGE */}
        <Route
          path="*"
          element={
            <div className="grid h-screen place-content-center bg-white px-4">
              <h1 className="uppercase tracking-widest text-gray-500">
                Not found
              </h1>
            </div>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
